import * as t from 'io-ts';

import {DeepReadonly, isDefined} from '../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {RConversationIdRef, RMessageUidRef} from '../../libs/shared/core/src/models/api/apiTypes';
import {MessageModel} from '../../libs/shared/core/src/models/api/messageModel';
import {makeOptional} from '../../libs/shared/core/src/models/api/types/optionalType';
import {CellsContext} from '../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {RActivityIdRef} from '../models/api/activityModel';
import {AuditTrailActivity} from '../models/api/auditTrailActivityModel';
import {ConversationBaseModel} from '../models/api/conversationModel';
import {CellsSortDirectionsEnum} from '../models/api/sortOptionModel';
import {
  InboxesConversationsParameters,
  InboxesTeammateMessagesParameters,
} from '../store/panels/inboxes/parameters/inboxesParameters';
import {
  CellsSortModesEnum,
  findParametersCellsSortMode,
} from '../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {makeConversationDateGetter, makeConversationPriorityDateGetter} from './models/conversationHelpers';
import {makeMessageDateGetter} from './models/messageHelpers';

/*
 * Runtime types.
 */

function makeCellsBoundary<T extends t.Mixed>(idType: T) {
  return t.type({
    priorityDate: makeOptional(t.number),
    date: t.number,
    id: idType,
  });
}

function makeCellsRange<T extends t.Mixed>(boundaryType: T) {
  return t.type({
    topBoundary: makeOptional(boundaryType),
    bottomBoundary: makeOptional(boundaryType),
  });
}

export const RCellsConversationsBoundary = makeCellsBoundary(RConversationIdRef);
export const RCellsConversationsRange = makeCellsRange(RCellsConversationsBoundary);

export const RCellsMessagesBoundary = makeCellsBoundary(RMessageUidRef);
export const RCellsMessagesRange = makeCellsRange(RCellsMessagesBoundary);

export const RCellsActivitiesBoundary = makeCellsBoundary(RActivityIdRef);
export const RCellsActivitiesRange = makeCellsRange(RCellsActivitiesBoundary);

/*
 * Types.
 */

export interface CellsBoundary<TId> {
  /** Used to prioritise cells based on a higher criterion (eg: SLA Timer). */
  readonly priorityDate: number | undefined;
  readonly date: number;
  readonly id: TId;
}

export type CellsBoundaryBuilder<TItem, TId> = (item: TItem) => CellsBoundary<TId>;

export interface CellsRange<TId> {
  readonly topBoundary: CellsBoundary<TId> | undefined;
  readonly bottomBoundary: CellsBoundary<TId> | undefined;
}

export interface CellsConversationsBoundary
  extends CellsBoundary<number>,
    DeepReadonly<t.TypeOf<typeof RCellsConversationsBoundary>> {}

export interface CellsConversationsRange
  extends CellsRange<number>,
    DeepReadonly<t.TypeOf<typeof RCellsConversationsRange>> {}

export type CellsConversationsBoundaryBuilder = CellsBoundaryBuilder<ConversationBaseModel, number>;

export interface CellsMessagesBoundary
  extends CellsBoundary<string>,
    DeepReadonly<t.TypeOf<typeof RCellsMessagesBoundary>> {}

export interface CellsMessagesRange
  extends CellsRange<string>,
    DeepReadonly<t.TypeOf<typeof RCellsMessagesRange>> {}

export interface CellsMessagesBoundaryBuilder extends CellsBoundaryBuilder<MessageModel, string> {
  (item: MessageModel): CellsBoundary<string>;
}

export interface CellsActivitiesBoundary
  extends CellsBoundary<number>,
    DeepReadonly<t.TypeOf<typeof RCellsActivitiesBoundary>> {}

export interface CellsActivitiesRange
  extends CellsRange<number>,
    DeepReadonly<t.TypeOf<typeof RCellsActivitiesRange>> {}

export interface CellsAuditTrailBoundaryBuilder extends CellsBoundaryBuilder<AuditTrailActivity, number> {
  (item: AuditTrailActivity): CellsActivitiesBoundary;
}

/*
 * Helpers.
 */

/** Make the boundary builder for the specified parameters. */
export function makeConversationBoundaryBuilder(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
): CellsConversationsBoundaryBuilder {
  const priorityDateGetter = makeConversationPriorityDateGetter(cellsContext, parameters);
  const dateGetter = makeConversationDateGetter(cellsContext, parameters);

  return (conversation) => ({
    priorityDate: priorityDateGetter(conversation),
    date: dateGetter(conversation),
    id: conversation.id,
  });
}

/** Make the boundary builder for the specified parameters. */
export function makeMessageBoundaryBuilder(
  parameters: InboxesTeammateMessagesParameters,
): CellsMessagesBoundaryBuilder {
  const dateGetter = makeMessageDateGetter(parameters);

  return (item) => ({
    priorityDate: undefined,
    date: dateGetter(item),
    id: item.uid,
  });
}

/** Make the boundary builder for the specified parameters. */
export const auditTrailBoundaryBuilder: CellsAuditTrailBoundaryBuilder = (item) => ({
  priorityDate: undefined,
  date: item.date,
  id: item.id,
});

/** Check if a given boundary item is without the bounds of the page. */
export function isWithinBounds<TId>(
  boundaries: CellsRange<TId>,
  sortDirection: CellsSortDirectionsEnum,
  item: CellsBoundary<TId>,
) {
  // Check both the top and bottom boundaries.
  const {topBoundary, bottomBoundary} = boundaries;
  const isWithinTop = compareBoundaries(sortDirection, topBoundary, item) <= 0;
  const isWithinBottom = compareBoundaries(sortDirection, bottomBoundary, item) >= 0;

  return isWithinTop && isWithinBottom;
}

export function compareBoundaries<TId>(
  sortDirection: CellsSortDirectionsEnum,
  a: CellsBoundary<TId> | undefined,
  b: CellsBoundary<TId> | undefined,
): number {
  // If the sort order is descending, we need to invert the results.
  const isDescending = sortDirection === CellsSortDirectionsEnum.DESCENDING;
  const multiplier = isDescending ? -1 : 1;

  if (!a || !b) {
    return 0;
  }

  // Compare priority dates, if provided.
  const hasPriorityDate = a.priorityDate !== undefined || b.priorityDate !== undefined;
  if (hasPriorityDate) {
    if (a.priorityDate === undefined || b.priorityDate === undefined) {
      throw new Error('This should not happen.');
    }

    if (a.priorityDate > b.priorityDate) {
      return multiplier;
    }

    if (a.priorityDate < b.priorityDate) {
      return -multiplier;
    }
  }

  // Compare by dates.
  if (a.date > b.date) {
    return multiplier;
  }

  if (a.date < b.date) {
    return -multiplier;
  }

  // If they're equal, compare by ID.
  if (a.id > b.id) {
    return multiplier;
  }

  if (a.id < b.id) {
    return -multiplier;
  }

  // Otherwise, they're actually equal.
  return 0;
}

export function sortConversations<T extends ConversationBaseModel>(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  conversations: ReadonlyArray<T>,
) {
  const isTrackerNeeded = findParametersCellsSortMode(parameters) === CellsSortModesEnum.TRACKER;

  const {sortDirection} = parameters;

  // Find the getter to use to extract a conversation's date.
  const buildBoundary = makeConversationBoundaryBuilder(cellsContext, parameters);

  // Perform the sort.
  return (
    conversations
      // Filter out conversations that are missing a tracker (if we need one) to avoid sorting by a bogus date.
      // TODO: Fix this better, see makeConversationDateGetter().
      .filter((c) => !isTrackerNeeded || isDefined(c.trackers[cellsContext.teammateId]))
      .sort((a, b) => compareBoundaries(sortDirection, buildBoundary(a), buildBoundary(b)))
  );
}
